import type { Dispatch, SetStateAction } from 'react';
import { Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { formatINR } from '@/utils/formatters';
import { unitLabel, type LineView } from './vendorPoLines';
export interface LineItem {
  description: string;
  hsnSac: string;
  /** null = blank: a suggested line with nothing short; it goes on the PO only once a qty is typed. */
  quantity: number | null;
  rate: number;
  gstRate: number;
  discount: number;
  productId: string;
  /** Shown, not stored: the vendor's quoted unit, else the product's own unit (SUN-240). */
  unit: string | null;
  /** 'vendor' = filled from the picked vendor's products; untouched ones are replaced when another vendor is picked. */
  source?: 'vendor' | 'manual';
  touched?: boolean;
  preferred?: boolean;
  note?: string | null;
}

type Totals = Record<'subtotal'|'totalItemDiscount'|'docDiscountAmt'|'docDiscountPct'|'totalGst'|'total', number>;
type Address = Record<string,string>;
interface AddressProps {
 billingAddress:Address;pickupAddress:Address;deliveryAddress:Address;
 setBillingAddress:(value:Address)=>void;setPickupAddress:(value:Address)=>void;setDeliveryAddress:(value:Address)=>void;inp:string;
}
export function PurchaseOrderAddresses({billingAddress,pickupAddress,deliveryAddress,setBillingAddress,setPickupAddress,setDeliveryAddress,inp}:AddressProps) {
 return (<div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-lg font-semibold text-[var(--text-primary)]">Addresses</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Vendor Billing Address */}
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Vendor Billing Address</h3>
              <div className="space-y-2">
                <input type="text" placeholder="Address Line 1" value={billingAddress?.line1 || ''} onChange={e => setBillingAddress({...billingAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={billingAddress?.line2 || ''} onChange={e => setBillingAddress({...billingAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={billingAddress?.city || ''} onChange={e => setBillingAddress({...billingAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={billingAddress?.state || ''} onChange={e => setBillingAddress({...billingAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={billingAddress?.pincode || ''} onChange={e => setBillingAddress({...billingAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={billingAddress?.country || 'India'} onChange={e => setBillingAddress({...billingAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
            {/* Pickup Address (Vendor's factory/warehouse) */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-medium text-[var(--text-secondary)]">Pickup Address</h3>
                <button type="button" onClick={() => setPickupAddress({...billingAddress})} className="text-[10px] text-[var(--ink-0)] hover:underline">Copy from Billing</button>
              </div>
              <div className="space-y-2">
                <input type="text" placeholder="Factory/Warehouse Address" value={pickupAddress?.line1 || ''} onChange={e => setPickupAddress({...pickupAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={pickupAddress?.line2 || ''} onChange={e => setPickupAddress({...pickupAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={pickupAddress?.city || ''} onChange={e => setPickupAddress({...pickupAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={pickupAddress?.state || ''} onChange={e => setPickupAddress({...pickupAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={pickupAddress?.pincode || ''} onChange={e => setPickupAddress({...pickupAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={pickupAddress?.country || 'India'} onChange={e => setPickupAddress({...pickupAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
            {/* Delivery Address (Our warehouse/site) */}
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Delivery Address (Our Site)</h3>
              <div className="space-y-2">
                <input type="text" placeholder="Warehouse/Site Address" value={deliveryAddress?.line1 || ''} onChange={e => setDeliveryAddress({...deliveryAddress, line1: e.target.value})} className={inp} />
                <input type="text" placeholder="Address Line 2" value={deliveryAddress?.line2 || ''} onChange={e => setDeliveryAddress({...deliveryAddress, line2: e.target.value})} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="City" value={deliveryAddress?.city || ''} onChange={e => setDeliveryAddress({...deliveryAddress, city: e.target.value})} className={inp} />
                  <input type="text" placeholder="State" value={deliveryAddress?.state || ''} onChange={e => setDeliveryAddress({...deliveryAddress, state: e.target.value})} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Pincode" value={deliveryAddress?.pincode || ''} onChange={e => setDeliveryAddress({...deliveryAddress, pincode: e.target.value})} className={inp} />
                  <input type="text" placeholder="Country" value={deliveryAddress?.country || 'India'} onChange={e => setDeliveryAddress({...deliveryAddress, country: e.target.value})} className={inp} />
                </div>
              </div>
            </div>
          </div>
        </div>);
}
export interface LineToggle { shortCount: number; mappedCount: number; view: LineView; onView: (view: LineView) => void }
interface ItemProps {
 items:LineItem[];setItems:Dispatch<SetStateAction<LineItem[]>>;emptyItem:()=>LineItem;
 setShowProducts:(value:boolean)=>void;updateItem:(index:number,field:keyof LineItem,value:string|number|null)=>void;
 removeItem:(index:number)=>void;rateHints:Record<string,string>;totals:Totals;
 lineToggle?:LineToggle|null;linesNote?:string;
}
const GST_RATES = [0,5,12,18,28];
const toggleBtn = (on: boolean) => `px-3 py-1 text-xs font-medium ${on ? 'bg-[var(--ink-0)] text-white' : 'text-gray-700 hover:bg-gray-50'}`;
export function PurchaseOrderItems({items,setItems,emptyItem,setShowProducts,updateItem,removeItem,rateHints,totals,lineToggle,linesNote}:ItemProps) {
 return (<div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowProducts(true)} className="flex items-center gap-1.5 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-3 py-1.5 text-sm font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors">
                <ShoppingBag size={14} /> Browse Products
              </button>
              <button onClick={() => setItems([...items, emptyItem()])} className="flex items-center gap-1 text-sm text-[var(--ink-0)] hover:underline">
                <Plus size={14} /> Add Item
              </button>
            </div>
          </div>
          {lineToggle && (
            <div className="mb-3 flex rounded-lg border border-gray-300 overflow-hidden w-fit" role="group" aria-label="Vendor products">
              <button type="button" aria-pressed={lineToggle.view === 'short'} onClick={() => lineToggle.onView('short')} className={toggleBtn(lineToggle.view === 'short')}>Only short ({lineToggle.shortCount})</button>
              <button type="button" aria-pressed={lineToggle.view === 'all'} onClick={() => lineToggle.onView('all')} className={toggleBtn(lineToggle.view === 'all')}>All mapped ({lineToggle.mappedCount})</button>
            </div>
          )}
          {linesNote && <p className="mb-3 text-xs text-gray-500">{linesNote}</p>}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500">
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2 w-24">HSN/SAC</th>
                  <th className="px-2 py-2 w-20">Qty</th>
                  <th className="px-2 py-2 w-16">Unit</th>
                  <th className="px-2 py-2 w-24">Rate</th>
                  <th className="px-2 py-2 w-20">Disc %</th>
                  <th className="px-2 py-2 w-20">GST %</th>
                  <th className="px-2 py-2 w-24 text-right">Amount</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const grossAmount = Number(((item.quantity ?? 0) * item.rate).toFixed(2));
                  const discAmount = Number((grossAmount * (item.discount || 0) / 100).toFixed(2));
                  const lineAmount = Number((grossAmount - discAmount).toFixed(2));
                  const lineGst = Number((lineAmount * (item.gstRate / 100)).toFixed(2));
                  return (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                        {item.preferred && <span className="text-amber-500" title="Preferred vendor for this product" aria-label="Preferred">★</span>}
                        <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Item description"
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input type="text" value={item.hsnSac} onChange={e => updateItem(idx, 'hsnSac', e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" aria-label="Qty" value={item.quantity ?? ''} onChange={e => updateItem(idx, 'quantity', e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        {item.note && <p className="mt-0.5 text-[10px] text-gray-500">{item.note}</p>}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-700" data-testid="line-unit">{unitLabel(item.unit)}</td>
                      <td className="px-2 py-2">
                        <input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        {item.productId && rateHints[item.productId] && <p className="mt-0.5 text-[10px] text-gray-500">{rateHints[item.productId]}</p>}
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" min={0} max={100} step="0.5" value={item.discount || ''} onChange={e => updateItem(idx, 'discount', parseFloat(e.target.value) || 0)}
                          placeholder="0" className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-center focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-2 py-2">
                        <select value={item.gstRate} onChange={e => updateItem(idx, 'gstRate', parseInt(e.target.value))}
                          className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-gray-900">{formatINR(lineAmount + lineGst)}</td>
                      <td className="px-2 py-2">
                        {(items.length > 1 || !!item.description) && (
                          <button type="button" aria-label="Remove line" onClick={() => removeItem(idx)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">{formatINR(totals.subtotal)}</span>
              </div>
              {totals.totalItemDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Item Discounts</span>
                  <span className="font-medium text-green-700">-{formatINR(totals.totalItemDiscount)}</span>
                </div>
              )}
              {totals.docDiscountAmt > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Vendor Discount ({totals.docDiscountPct?.toFixed(1)}%)</span>
                  <span className="font-medium text-green-700">-{formatINR(totals.docDiscountAmt)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">GST</span>
                <span className="font-medium text-gray-900">{formatINR(totals.totalGst)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 text-base">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-gray-900">{formatINR(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>);
}
interface DiscountProps {docDiscountType:'amount'|'percentage';setDocDiscountType:(value:'amount'|'percentage')=>void;docDiscountValue:number;setDocDiscountValue:(value:number)=>void;totals:Totals;}
export function PurchaseOrderDiscount({docDiscountType,setDocDiscountType,docDiscountValue,setDocDiscountValue,totals}:DiscountProps) {
 return (<div className="rounded-lg border border-dashed border-green-300 bg-green-50 p-4">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-sm font-semibold text-green-800">Vendor Discount</h4>
            <div className="flex rounded-lg border border-green-300 overflow-hidden">
              <button onClick={() => setDocDiscountType('amount')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'amount' ? 'bg-green-600 text-white' : 'text-green-700 hover:bg-green-100'}`}>
                &#8377; Amount
              </button>
              <button onClick={() => setDocDiscountType('percentage')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'percentage' ? 'bg-green-600 text-white' : 'text-green-700 hover:bg-green-100'}`}>
                % Percentage
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="number" min="0" value={docDiscountValue || ''} onChange={e => setDocDiscountValue(parseFloat(e.target.value) || 0)}
              placeholder={docDiscountType === 'amount' ? 'Discount amount' : '% Discount'}
              className="w-48 rounded-lg border border-green-300 px-3 py-2 text-sm" />
            <span className="text-sm text-green-700">
              {docDiscountType === 'amount'
                ? `(${totals.docDiscountPct?.toFixed(2) || 0}%)`
                : `(${formatINR(totals.docDiscountAmt || 0)})`}
            </span>
          </div>
        </div>);
}
